
import { useEffect, useState, useRef, useCallback } from "react"
import Slider from "react-slick"
import moment from "moment"
import AxiosCall from "../../config/axios"
import "slick-carousel/slick/slick.css"
import "slick-carousel/slick/slick-theme.css"

export default function SignagePlaylist(props) {
  const sliderRef = useRef(null)
  const videoRefs = useRef({})
  const intervalRef = useRef(null)
  const debugRef = useRef([]) // Use ref instead of state for debug logs

  const [contents, setContents] = useState([])
  const [loading, setLoading] = useState(false)
  const [activeSlide, setActiveSlide] = useState(0)
  const [sliderHeight, setSliderHeight] = useState("100vh")

  // Create settings object once and store in ref to avoid re-renders
  const settingsRef = useRef({
    dots: false,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    fade: true,
    autoplaySpeed: 3000, // Default value (3 seconds)
    pauseOnHover: false,
    className: "x-slick",
    beforeChange: (current, next) => {
      // Reset video playback when changing slides
      if (videoRefs.current[current]) {
        videoRefs.current[current].currentTime = 0
        videoRefs.current[current].pause()
        videoRefs.current[current].isPlaying = false
      }
    },
    afterChange: (current) => {
      setActiveSlide(current)
      // Update autoplay speed based on current content's duration
      if (sliderRef.current && contents[current]) {
        try {
          const currentContent = contents[current]
          const duration = currentContent?.duration ? currentContent.duration * 1000 : 3000 // Convert to milliseconds
          
          // Update the settings ref with the new duration
          settingsRef.current = {
            ...settingsRef.current,
            autoplaySpeed: duration
          }
          
          // Force the slider to use the new settings
          sliderRef.current.slickPlay()
          console.log('Set slide duration to:', duration, 'ms')
        } catch (err) {
          console.error("Error setting autoplay speed:", err)
        }
      }
    }
  })

  // Update slider settings when contents change
  useEffect(() => {
    if (sliderRef.current && contents.length > 0) {
      try {
        // Update settings with the latest callbacks that have access to current contents
        const firstContent = contents[0]
        const duration = firstContent?.duration ? firstContent.duration * 1000 : 3000 // Convert to milliseconds
        
        // Update the settings ref with the new duration
        settingsRef.current = {
          ...settingsRef.current,
          autoplaySpeed: duration
        }
        
        // Force slider to re-render with new settings
        sliderRef.current.slickGoTo(0)
      } catch (err) {
        console.error("Error updating slider settings:", err)
      }
    }
  }, [contents])

  // Handle video playback for a specific slide
  const handleVideoPlayback = useCallback(
    (index) => {
      if (!contents[index]) return

      const isVideo = contents[index].url.includes("video")

      if (isVideo && videoRefs.current[index]) {
        const video = videoRefs.current[index]
        const nextIndex = (index + 1) % contents.length
        const nextContent = contents[nextIndex]

        // Prevent multiple transitions
        if (videoRefs.current[index].isPlaying) return
        videoRefs.current[index].isPlaying = true

        try {
          // Reset video and ensure it's loaded
          video.currentTime = 0
          video.load()

          // Disable autoplay and pause slider
          if (sliderRef.current) {
            sliderRef.current.slickPause()
          }

          // Play the video with error handling
          const playPromise = video.play()

          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                // Listen for video end to resume slider
                video.onended = () => {
                  videoRefs.current[index].isPlaying = false
                  if (sliderRef.current) {
                    // Get the next content's duration or use default
                    const nextContent = contents[(index + 1) % contents.length]
                    const duration = nextContent?.duration ? nextContent.duration * 1000 : 3000
                    
                    // Update settings for the next slide
                    settingsRef.current = {
                      ...settingsRef.current,
                      autoplaySpeed: duration
                    }
                    
                    // Move to next slide
                    sliderRef.current.slickNext()
                  }
                }
              })
              .catch((err) => {
                console.error("Video play error:", err)
                videoRefs.current[index].isPlaying = false
                if (sliderRef.current) {
                  sliderRef.current.slickPlay()
                  sliderRef.current.slickNext()
                }
              })
          }
        } catch (err) {
          if (sliderRef.current) {
            sliderRef.current.slickPlay()
            sliderRef.current.slickNext()
          }
          videoRefs.current[index].isPlaying = false
        }
      } else {
        // For non-video content, just move to next slide
        if (sliderRef.current) {
          sliderRef.current.slickNext()
        }
      }
    },
    [contents],
  )

  // Handle fetching playlist data
  const getScreens = async () => {
    if (loading) return

    setLoading(true)
    try {
      const callObj = {
        method: "GET",
        path: `signage/fetchPlaylist?slug=${props.match.params?.slug}`,
      }

      const res = await AxiosCall(callObj)
      const filteredContents = processScreenContents(res.screens)

      // If only one content, duplicate it to ensure continuous playback
      if (filteredContents.length === 1) {
        setContents([...filteredContents, ...filteredContents])
      } else {
        setContents(filteredContents)
      }
    } catch (error) {
      console.error("Error fetching playlist:", error)
    } finally {
      setLoading(false)
    }
  }

  // Process and filter screen contents based on scheduling rules
  const processScreenContents = useCallback((screens) => {
    const now = moment()
    const today = now.format("dddd").toLowerCase()
    const result = []

    screens.forEach((screen) => {
      screen.folders.forEach((folder) => {
        folder.contents.forEach((content) => {
          // Check day constraints
          let dayMatch = true
          if (content.days?.length > 0) {
            dayMatch = content.days.includes(today)
          }

          // Check time constraints
          let timeMatch = true
          if (content.from && content.to) {
            const from = moment().set({
              hour: Number.parseInt(content.from.split(":")[0]),
              minute: Number.parseInt(content.from.split(":")[1]),
            })

            const to = moment().set({
              hour: Number.parseInt(content.to.split(":")[0]),
              minute: Number.parseInt(content.to.split(":")[1]),
            })

            timeMatch = now.isBetween(from, to)
          }

          // Add content if it matches scheduling criteria
          if (dayMatch && timeMatch) {
            result.push({ ...content, size: screen.size })
          }
        })
      })
    })

    return result
  }, [])

  // Set up initial data fetch and refresh interval
  useEffect(() => {
    // Initial fetch
    getScreens()

    // Set up refresh interval (1800000 milliseconds = 30 minutes)
    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => {
        // Only refresh if we have active contents
        if (contents.length > 0) {
          getScreens()
        }
      }, 1800000)
    }
    
    // Cleanup interval on component unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    // Cleanup interval on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [])  // Empty dependency array to prevent re-running


  // Preload images to get their dimensions
  useEffect(() => {
    if (contents.length) {
      // Preload all images to ensure consistent height
      const imageUrls = contents.filter((c) => !c.url.includes("video")).map((c) => c.url)

      if (imageUrls.length) {
        const preloadImages = imageUrls.map((url) => {
          return new Promise((resolve) => {
            const img = new Image()
            img.onload = () => resolve()
            img.onerror = () => resolve() // Continue even if image fails to load
            img.src = url
          })
        })

        Promise.all(preloadImages).then(() => {
          // All images are loaded, now we can set a fixed height
          setSliderHeight("100vh")
        })
      }
    }
  }, [contents])

  // When component mounts, check if we need to play the initial video
  useEffect(() => {
    if (contents.length > 0 && activeSlide === 0) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        handleVideoPlayback(0)
      }, 500)

      return () => clearTimeout(timer)
    }
  }, [contents, activeSlide, handleVideoPlayback])

  // Update slider event handlers when contents change
  useEffect(() => {
    if (contents.length > 0) {
      // Update settings with the latest callbacks that have access to current contents
      settingsRef.current = {
        ...settingsRef.current,
        beforeChange: (current, next) => {
          // Pause current video before slide changes
          if (videoRefs.current[current]) {
            try {
              videoRefs.current[current].pause()
            } catch (err) {
            }
          }
        },
        afterChange: (current) => {
          setActiveSlide(current)
          handleVideoPlayback(current)
        },
      }
    }
  }, [contents, handleVideoPlayback])
// Handle video element refs
const setVideoRef = (el, index) => {
    if (el) {
      videoRefs.current[index] = el
    }
}

  return (
    <div className="x-signage-playlist" style={{ backgroundColor: "black" }}>
      <div style={{ height: sliderHeight, overflow: "hidden" }}>
        {contents.length > 0 && (
          <Slider ref={sliderRef} {...settingsRef.current}>
            {contents.map((content, index) => (
              <div key={`content-${index}`} style={{ height: sliderHeight }}>
                {content.url.includes("video") ? (
                  <video
                    ref={(el) => setVideoRef(el, index)}
                    muted
                    playsInline
                    autoPlay={index === activeSlide}
                    preload="auto"
                    width="100%"
                    height="100%" 
                    style={{ objectFit: "contain" }}
                    onError={() => console.error(`Video error at index ${index}`)}
                    onLoadedData={() => console.log(`Video loaded at index ${index}`)}
                    onPlay={() => console.log(`Video started playing at index ${index}`)}
                    onEnded={() => {
                      console.log(`Video ended at index ${index}`)
                      if (sliderRef.current) {
                        sliderRef.current.slickPlay()
                      }
                    }}
                  >
                    <source src={content.url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <img
                      src={content.url || "/placeholder.svg"}
                      alt={`Slide ${index + 1}`}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        margin: "0 auto", // Center horizontally
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
          </Slider>
        )}
      </div>
    </div>
  )
}
